import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Calendar, Clock, Video, Building, FileText, Download, CreditCard,
  Loader2, Printer, Archive, ChevronDown, ChevronUp, Eye, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { BRAND } from '../data/mock';
import { getDashboardData, getDownloadUrl, createRazorpayOrder, verifyRazorpayPayment } from '../services/api';

const APPOINTMENT_STATUSES = {
  REQUESTED: { label: 'Requested', color: 'bg-amber-100 text-amber-800' },
  PENDING: { label: 'Pending Approval', color: 'bg-blue-100 text-blue-800' },
  CONFIRMED: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800' },
  RESCHEDULED: { label: 'Rescheduled', color: 'bg-purple-100 text-purple-800' },
  REJECTED: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  COMPLETED: { label: 'Completed', color: 'bg-gray-100 text-gray-800' },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600' }
};

const PAYMENT_STATUSES = {
  PENDING: { label: 'Payment Pending', color: 'bg-amber-100 text-amber-800' },
  PAID_ONLINE: { label: 'Paid Online', color: 'bg-emerald-100 text-emerald-800' },
  PAYMENT_PENDING: { label: 'Pay at Clinic', color: 'bg-blue-100 text-blue-800' }
};

const PatientDashboard = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [error, setError] = useState(null);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [bookingId, token]);

  const fetchDashboardData = async () => {
    try {
      const data = await getDashboardData(bookingId, token);
      setDashboardData(data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const canJoinMeeting = (apt) => {
    if (apt.consultation_type !== 'ONLINE' || apt.status !== 'CONFIRMED') return false;
    
    const appointmentDateTime = new Date(`${apt.appointment_date}T${apt.time_slot}`);
    const now = new Date();
    const diffMinutes = (appointmentDateTime - now) / (1000 * 60);
    
    return diffMinutes <= 15 && diffMinutes >= -30;
  };

  const handlePayNow = async (apt) => {
    try {
      const orderData = await createRazorpayOrder(apt.booking_id);
      
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "JB's Homoeopathy",
        description: `Consultation Fee - ${apt.consultation_type === 'ONLINE' ? 'Online' : 'In-Clinic'}`,
        order_id: orderData.order_id,
        prefill: {
          name: orderData.patient_name,
          email: orderData.patient_email,
          contact: orderData.patient_phone
        },
        theme: {
          color: '#2F5233'
        },
        handler: async function (response) {
          try {
            await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              appointment_id: apt.booking_id
            });
            
            toast({
              title: "Payment Successful!",
              description: "Your payment has been confirmed."
            });
            
            // Refresh dashboard data
            fetchDashboardData();
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast({
              title: "Payment Verification Failed",
              description: "Please contact support if amount was deducted.",
              variant: "destructive"
            });
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (err) {
      console.error('Error creating Razorpay checkout:', err);
    }
  };

  const handleDownloadReport = async (s3Key, fileName) => {
    try {
      const result = await getDownloadUrl(s3Key);
      window.open(result.download_url, '_blank');
    } catch (err) {
      console.error('Error getting download URL:', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDF9F5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#2F5233] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDF9F5] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchDashboardData}>Try Again</Button>
        </div>
      </div>
    );
  }

  const { upcoming_appointments = [], past_appointments = [], reports = [], payment_history = [], total_spent = 0 } = dashboardData || {};

  return (
    <div className="min-h-screen bg-[#FDF9F5] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-serif font-bold text-[#2F5233]">Patient Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your appointments with {BRAND.doctorName}</p>
          </div>
          <div className="flex space-x-3 mt-4 md:mt-0">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Total Appointments</p>
              <p className="text-2xl font-bold text-[#2F5233]">{upcoming_appointments.length + past_appointments.length}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Upcoming</p>
              <p className="text-2xl font-bold text-blue-600">{upcoming_appointments.length}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-emerald-600">
                {past_appointments.filter(a => a.status === 'COMPLETED').length}
              </p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900">₹{total_spent.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="upcoming" className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="upcoming">Upcoming ({upcoming_appointments.length})</TabsTrigger>
            <TabsTrigger value="past">Past ({past_appointments.length})</TabsTrigger>
            <TabsTrigger value="reports">My Reports</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
          </TabsList>

          {/* Upcoming Appointments */}
          <TabsContent value="upcoming">
            {upcoming_appointments.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No upcoming appointments</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {upcoming_appointments.map((apt) => (
                  <Card key={apt.booking_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start space-x-4">
                          {apt.consultation_type === 'ONLINE' ? (
                            <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                              <Video className="w-7 h-7 text-blue-600" />
                            </div>
                          ) : (
                            <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                              <Building className="w-7 h-7 text-emerald-600" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="font-semibold text-gray-900">
                                {apt.consultation_type === 'ONLINE' ? 'Online Consultation' : 'In-Clinic Visit'}
                              </p>
                              <Badge className={APPOINTMENT_STATUSES[apt.status]?.color}>
                                {APPOINTMENT_STATUSES[apt.status]?.label}
                              </Badge>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {formatDate(apt.appointment_date)}
                              </span>
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {formatTime(apt.time_slot)}
                              </span>
                            </div>
                            {apt.reason && (
                              <p className="text-sm text-gray-500 mt-2">{apt.reason}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col md:items-end space-y-2">
                          <div className="flex items-center space-x-2">
                            <Badge className={PAYMENT_STATUSES[apt.payment_status]?.color}>
                              {PAYMENT_STATUSES[apt.payment_status]?.label}
                            </Badge>
                            <span className="font-bold text-gray-900">₹{apt.amount?.toLocaleString()}</span>
                          </div>
                          
                          <div className="flex space-x-2">
                            {canJoinMeeting(apt) && apt.meeting_link && (
                              <a href={apt.meeting_link} target="_blank" rel="noopener noreferrer">
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                                  <Video className="w-4 h-4 mr-2" />
                                  Join Meeting
                                </Button>
                              </a>
                            )}
                            {apt.payment_status === 'PENDING' && (
                              <Button size="sm" className="bg-[#E91E8C] hover:bg-[#E91E8C]/90" onClick={() => handlePayNow(apt)}>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Pay Now
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Uploaded Reports */}
                      {apt.uploaded_reports?.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <button
                            onClick={() => setExpandedReport(expandedReport === apt.booking_id ? null : apt.booking_id)}
                            className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
                          >
                            <FileText className="w-4 h-4" />
                            <span>{apt.uploaded_reports.length} Report(s) Uploaded</span>
                            {expandedReport === apt.booking_id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          
                          {expandedReport === apt.booking_id && (
                            <div className="mt-3 space-y-2">
                              {apt.uploaded_reports.map((report, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center space-x-3">
                                    <FileText className="w-5 h-5 text-[#2F5233]" />
                                    <span className="text-sm font-medium">{report.file_name}</span>
                                  </div>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleDownloadReport(report.s3_key, report.file_name)}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Past Appointments */}
          <TabsContent value="past">
            {past_appointments.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No past appointments</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {past_appointments.map((apt) => (
                  <Card key={apt.booking_id} className="border-0 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start space-x-4">
                          {apt.consultation_type === 'ONLINE' ? (
                            <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                              <Video className="w-7 h-7 text-gray-500" />
                            </div>
                          ) : (
                            <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                              <Building className="w-7 h-7 text-gray-500" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="font-semibold text-gray-700">
                                {apt.consultation_type === 'ONLINE' ? 'Online Consultation' : 'In-Clinic Visit'}
                              </p>
                              <Badge className={APPOINTMENT_STATUSES[apt.status]?.color}>
                                {APPOINTMENT_STATUSES[apt.status]?.label}
                              </Badge>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {formatDate(apt.appointment_date)}
                              </span>
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {formatTime(apt.time_slot)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className="font-bold text-gray-600">₹{apt.amount?.toLocaleString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-serif">My Medical Reports</CardTitle>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No reports uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reports.map((report, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-[#2F5233]/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#2F5233]" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{report.file_name}</p>
                            <p className="text-sm text-gray-500">
                              Appointment: {report.appointment_id}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadReport(report.s3_key, report.file_name)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-serif">Payment History</CardTitle>
              </CardHeader>
              <CardContent>
                {payment_history.length === 0 ? (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No payment history</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payment_history.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Appointment: {payment.appointment_id}</p>
                            <p className="text-sm text-gray-500">{formatDate(payment.created_at)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">₹{payment.amount?.toLocaleString()}</p>
                          <Badge className={payment.status === 'PAID' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                            {payment.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PatientDashboard;
